if (!customElements.get('product-bundle-set')) {
  customElements.define(
    'product-bundle-set',
    class ProductBundleSet extends HTMLElement {
      constructor() {
        super();   

        if (this.dataset.bundleSoldOut === 'true') return;

        this.discount = parseFloat(this.dataset.discount) || 0;
        this.pageProductId = this.dataset.pageProductId || '';
        this.button = this.querySelector('[data-bundle-cta]');
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.defaultButtonLabel = this.button?.textContent?.trim() || '';
        this.defaultErrorMessage = this.errorEl?.textContent?.trim() || '';

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-bundle-variant]')) this.onChange();
        });

        this.addEventListener('input', (event) => {
          if (event.target.matches('[data-bundle-variant]')) this.onChange();
        });

        this.button?.addEventListener('click', (event) => this.onAdd(event));
        this.onChange();
      }

      getVariantInputs() {
        return this.querySelectorAll(
          '[data-bundle-item]:not([data-product-id="' + this.pageProductId + '"]) [data-bundle-variant]'
        );
      }

      getOptionFromInput(input) {
        if (input.tagName === 'SELECT') {
          const variantId = input.value;
          if (!variantId) return null;

          return [...input.options].find((option) => option.value === variantId) || null;
        }

        if (!input.value) return null;

        return input;
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents);
        }
        return (cents / 100).toFixed(2);
      }

      getSelectedVariants() {
        return [...this.getVariantInputs()].map((input) => {
          const itemEl = input.closest('[data-bundle-item]');
          if (!itemEl || itemEl.dataset.productId === this.pageProductId) return null;

          const itemPriceEl = itemEl.querySelector('[data-item-price]');
          const itemCompareEl = itemEl.querySelector('[data-item-compare]');
          const option = this.getOptionFromInput(input);

          if (!option) return null;

          return {
            id: option.value,
            price: parseInt(option.dataset.price, 10) || 0,
            compareAtPrice: parseInt(option.dataset.compareAtPrice, 10) || 0,
            available: option.dataset.available === 'true',
            itemPriceEl,
            itemCompareEl,
          };
        });
      }

      updateButtonState() {
        if (!this.button || this.button.classList.contains('success')) return;

        const variants = this.getSelectedVariants();
        const allSelected = variants.length > 0 && variants.every((variant) => variant !== null);
        const allAvailable = allSelected && variants.every((variant) => variant.available);

        this.button.classList.toggle('disabled', !allSelected || !allAvailable);
        this.button.disabled = !allSelected || !allAvailable;
      }

      onChange() {
        const variants = this.getSelectedVariants();
        const allSelected = variants.length > 0 && variants.every((variant) => variant !== null);
        const allAvailable = allSelected && variants.every((variant) => variant.available);

        variants.forEach((variant) => {
          if (!variant || !variant.itemPriceEl) return;

          // Shop money_format may include HTML (e.g. <span class=money>), so use innerHTML.
          variant.itemPriceEl.innerHTML = this.formatMoney(variant.price);

          if (variant.itemCompareEl) {
            if (variant.compareAtPrice > variant.price) {
              variant.itemCompareEl.innerHTML = this.formatMoney(variant.compareAtPrice);
              variant.itemCompareEl.hidden = false;
            } else {
              variant.itemCompareEl.hidden = true;
            }
          }
        });

        this.getVariantInputs().forEach((input) => {
          if (input.tagName !== 'SELECT' || input.value) return;

          const itemEl = input.closest('[data-bundle-item]');
          const itemPriceEl = itemEl?.querySelector('[data-item-price]');
          const itemCompareEl = itemEl?.querySelector('[data-item-compare]');

          if (itemPriceEl) itemPriceEl.textContent = '—';
          if (itemCompareEl) itemCompareEl.hidden = true;
        });

        const combinedCents = variants.reduce((total, variant) => {
          return variant ? total + variant.price : total;
        }, 0);

        const discountAmount = Math.round(combinedCents * (this.discount / 100));
        const setPriceCents = combinedCents - discountAmount;

        const combinedEl = this.querySelector('[data-bundle-combined]');
        const setPriceEl = this.querySelector('[data-bundle-set-price]');

        if (combinedEl) {
          combinedEl.innerHTML = allSelected
            ? `Combined price: ${this.formatMoney(combinedCents)}`
            : 'Combined price: —';
        }
        if (setPriceEl) setPriceEl.innerHTML = allSelected ? this.formatMoney(setPriceCents) : '—';

        if (this.errorEl) {
          if (allSelected && !allAvailable) {
            this.errorEl.textContent = this.dataset.soldOutMessage || 'One or more selected variants are sold out.';
            this.errorEl.classList.add('show');
          } else {
            this.errorEl.textContent = this.defaultErrorMessage;
            this.errorEl.classList.remove('show');
          }
        }

        if (this.button && !this.button.classList.contains('success')) {
          this.button.classList.toggle('disabled', !allSelected || !allAvailable);
          this.button.disabled = !allSelected || !allAvailable;
        }
      }

      renderCartContents(responseData) {
        if (!this.cart) return;

        const isCartNotification = this.cart.tagName.toLowerCase() === 'cart-notification';
        const addedItems = responseData.items || (responseData.key ? [responseData] : []);

        if (isCartNotification && addedItems.length > 0 && responseData.sections) {
          this.cart.getSectionsToRender().forEach((section) => {
            if (section.id === 'cart-notification-product') {
              const target = document.getElementById(section.id);
              if (target) this.renderBundleNotificationSummary(target);
              return;
            }

            const sectionHtml = responseData.sections[section.id];
            if (!sectionHtml) return;
            const target = document.getElementById(section.id);
            if (target) {
              target.innerHTML = this.cart.getSectionInnerHTML(sectionHtml, section.selector || '.shopify-section');
            }
          });

          if (this.cart.header) this.cart.header.reveal();
          this.cart.open();
          return;
        }

        const normalizedResponse = { ...responseData };
        if (!normalizedResponse.key && addedItems.length > 0) {
          normalizedResponse.key = addedItems[addedItems.length - 1].key;
        }
        if (!normalizedResponse.id && addedItems.length > 0) {
          normalizedResponse.id = addedItems[addedItems.length - 1].id;
        }

        this.cart.renderContents(normalizedResponse);
      }

      renderBundleNotificationSummary(target) {
        const summary = document.createElement('div');
        summary.className = 'cart-item cart-item--bundle-summary';

        const bundleTitle = this.dataset.bundleTitle || 'Product Bundle';
        const bundleImage = this.dataset.bundleImage;

        if (bundleImage) {
          const imageWrapper = document.createElement('div');
          imageWrapper.className = 'cart-notification-product__image bundle-notification-summary__image global-media-settings';

          const image = document.createElement('img');
          image.src = bundleImage;
          image.alt = bundleTitle;
          image.width = 88;
          image.height = 88;
          image.loading = 'lazy';
          imageWrapper.appendChild(image);
          summary.appendChild(imageWrapper);
        }

        const details = document.createElement('div');
        details.className = 'bundle-notification-summary__details';

        const badge = document.createElement('span');
        badge.className = 'bundle-notification-summary__badge';
        badge.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 7.5 12 3l8 4.5-8 4.5-8-4.5Z"></path>
            <path d="m4 12 8 4.5 8-4.5M4 16.5l8 4.5 8-4.5"></path>
          </svg>
          <span>Curated Bundle</span>
        `;
        details.appendChild(badge);

        const title = document.createElement('h3');
        title.className = 'cart-notification-product__name h4';
        title.textContent = bundleTitle;
        details.appendChild(title);

        summary.appendChild(details);
        target.replaceChildren(summary);
      }

      async onAdd(event) {
        event.preventDefault();

        const variants = this.getSelectedVariants();
        const allSelected = variants.length > 0 && variants.every((variant) => variant !== null);

        if (!allSelected) {
          this.errorEl?.classList.add('show');
          return;
        }

        const unavailable = variants.find((variant) => !variant.available);
        if (unavailable) {
          if (this.errorEl) {
            this.errorEl.textContent = this.dataset.soldOutMessage || 'One or more selected variants are sold out.';
            this.errorEl.classList.add('show');
          }
          return;
        }

        const items = variants.map((variant) => ({
          id: parseInt(variant.id, 10),
          quantity: 1,
        }));

        try {
          this.button?.classList.add('loading');
          this.button?.setAttribute('disabled', 'disabled');

          const formData = new FormData();
          items.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
          });

          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(this.button || document.activeElement);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          const response = await fetch(window.routes.cart_add_url, config);
          const responseData = await response.json();

          if (responseData.status) {
            if (this.errorEl) {
              this.errorEl.textContent = responseData.description || responseData.message || 'Unable to add bundle to cart.';
              this.errorEl.classList.add('show');
            }
            return;
          }

          if (!this.cart) {
            window.location.href = window.routes?.cart_url || '/cart';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-bundle-set',
              cartData: responseData,
            });
          }

          if (this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }

          this.renderCartContents(responseData);

          const successLabel = this.dataset.successLabel || '✓  Both Items Added to Cart';
          const originalLabel = this.defaultButtonLabel;

          this.button?.classList.add('success');
          this.button?.classList.remove('disabled');
          if (this.button) {
            this.button.textContent = successLabel;
            this.button.disabled = true;
          }

          window.setTimeout(() => {
            if (!this.button) return;
            this.button.textContent = originalLabel;
            this.button.classList.remove('success');
            this.onChange();
          }, 2800);
        } catch (error) {
          console.error(error);
          if (this.errorEl) {
            this.errorEl.textContent = 'Network error — please try again.';
            this.errorEl.classList.add('show');
          }
        } finally {
          this.button?.classList.remove('loading');
          if (this.button && !this.button.classList.contains('success')) {
            this.updateButtonState();
          }
        }
      }
    }
  );
}
